import type { StoreActions, StoreGetters } from "@/types/pinia-helpers";
import type { TwitchDataTypes } from "@/types/twitch/TwitchDataTypes";
import Config from "@/utils/Config";
import { toast } from "@/utils/toast/toast";
import TwitchUtils from "@/utils/twitch/TwitchUtils";
import { acceptHMRUpdate, defineStore } from "pinia";
import DataStore from "../DataStore";
import type { IRewardsActions, IRewardsGetters, IRewardsState } from "../StoreProxy";
import StoreProxy from "../StoreProxy";

export const storeRewards = defineStore("rewards", {
	state: (): IRewardsState => ({
		rewardList: [],
		powerUpList: [],
		jumpscareReward: {},
	}),

	getters: {} satisfies StoreGetters<IRewardsGetters, IRewardsState>,

	actions: {
		populateData(): void {
			const json = DataStore.get(DataStore.JUMPSCARE_REWARDS);
			if (json) {
				this.jumpscareReward = JSON.parse(json) as IRewardsState["jumpscareReward"];
			}
		},

		async loadRewards(): Promise<TwitchDataTypes.Reward[]> {
			try {
				this.rewardList = await TwitchUtils.getRewards(true);
			} catch (error) {
				this.rewardList = [];
				console.log(error);
				toast(StoreProxy.i18n.t("error.rewards_loading"), { type: "error" });
				return [];
			}

			//Sort by cost and name
			this.rewardList = this.rewardList.sort((a, b) => {
				if (a.cost < b.cost) return -1;
				if (a.cost > b.cost) return 1;
				if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
				if (a.title.toLowerCase() > b.title.toLowerCase()) return 1;
				return 0;
			});

			//Push "Highlight my message" reward as it's not given by the API...
			this.rewardList.unshift(Config.instance.highlightMyMessageReward);

			//Push "All rewards" item for triggers. This needs to be filtered out where unnecessary
			this.rewardList.unshift(Config.instance.allRewards);

			return this.rewardList;
		},
		async loadPowerUps(): Promise<TwitchDataTypes.CustomPowerUp[]> {
			try {
				this.powerUpList = await TwitchUtils.getCustomPowerUps();
			} catch (error) {
				this.powerUpList = [];
				console.log(error);
				toast(StoreProxy.i18n.t("error.powerups_loading"), { type: "error" });
				return [];
			}

			//Sort by cost and name
			this.powerUpList = this.powerUpList.sort((a, b) => {
				if (a.bits < b.bits) return -1;
				if (a.bits > b.bits) return 1;
				if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
				if (a.title.toLowerCase() > b.title.toLowerCase()) return 1;
				return 0;
			});

			// Add static default power ups
			// this.powerUpList.unshift(Config.instance.messageEffectPowerup);
			// this.powerUpList.unshift(Config.instance.gigantifiedEmotePowerup);
			// this.powerUpList.unshift(Config.instance.celebrationPowerup);
			this.powerUpList.unshift(Config.instance.allPowerups);

			return this.powerUpList;
		},

		setJumpscareReward(rewardId: string, enabled: boolean): void {
			//Only keep enabled entries to avoid bloating the storage
			if (enabled) this.jumpscareReward[rewardId] = true;
			else delete this.jumpscareReward[rewardId];
			this.saveData();
		},

		saveData(): void {
			DataStore.set(DataStore.JUMPSCARE_REWARDS, this.jumpscareReward);
		},
	} satisfies StoreActions<"rewards", IRewardsState, IRewardsGetters, IRewardsActions>,
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(storeRewards, import.meta.hot));
}
